import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { UserPlus } from 'lucide-react';
import { ToastType, useToast } from '../toast/ToastProvider';
import PatientService from '../../services/patientService';

import { Patient } from '../../types/Patient';
import { UtilsService } from '../../services/utilsService';
import ConfirmModal from '../../modals/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { WhatsAppMessageTypes } from '../../types/WhatsAppMessage';
import WhatsAppApiService from '../../services/whatsAppApiService';
import SelectCustom from '../select-custom/SelectCustom';


const Patients = () => {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPatientIndex, setSelectedPatientIndex] = useState<number>(-1);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [showDelteConfirm, setShowDeleteConfirm] = useState(false);
    const { showToast } = useToast();
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'he';
    const activeClinicId = sessionStorage.getItem('activeClinicId') || '';

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        birthDate: '',
        email: ''
    });

    // Pagination
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [totalPatients, setTotalPatients] = useState(0);

    const pageSizeOptions = [
        { value: 10, label: "10" },
        { value: 25, label: "25" },
        { value: 50, label: "50" },
        { value: 100, label: "100" },
    ];

    useEffect(() => {
        fetchPatients(currentPage);
    }, [currentPage, pageSize]);

    const fetchPatients = async (page = 1, search = "") => {
        const controller = new AbortController();

        try {
            const response = await PatientService.getAllPatients(controller.signal, page, pageSize, search);
            const list = Array.isArray(response.data) ? response.data : [];
            const formattedPatients = UtilsService.formatDateField(list, 'birthDate');
            setPatients(formattedPatients);
            const pagination = response.pagination;

            if (pagination) {
                setTotalPages(pagination.totalPages);
                setCurrentPage(pagination.page);
                setTotalPatients(pagination.total);
            }
        } 
        catch (error: any) {
            showToast(error.response?.data?.message || 'Failed to fetch patients', ToastType.Error);
        }
    };

    const handleAddPatient = async () => {
        const controller = new AbortController();

        try {
            if (!UtilsService.validatePhoneNumber(formData.phone)) {
                showToast(t(`toastMessages.FILL_VALID_PHONE`), ToastType.Warning);
                return;
            }

            formData.phone = UtilsService.normalizeIsraeliPhoneNumber(formData.phone) || '';
            const newPatient = await PatientService.createPatient({ ...formData, clinicId: activeClinicId }, controller.signal);
            
            if (newPatient?.error) {
                const code = newPatient.error?.response?.data?.code;
                const message = newPatient.message;
                showToast(code ? t(`patientMessages.${code}`) : message || 'Failed to add patient', ToastType.Warning);
                return;
            }
            
            // ✅ Success
            setPatients(prevPatients => [...prevPatients, newPatient.data]);
            setTotalPatients(prevTotal => prevTotal + 1); // Update optimistic totalPatients for pagination
            setTotalPages(Math.ceil((totalPatients+1) / pageSize)); // Update optimistic totalPages for pagination
            showToast(t(`patientMessages.${newPatient.code}`), ToastType.Success);
            
            setShowInviteModal(false);
            resetForm();
        } catch (error: any) {
            const code = error?.response?.data?.code;
            showToast(code ? t(`patientMessages.${code}`) : 'Failed to add patient', ToastType.Error);
        }
    };

    const handleEditPatient = (index: number) => {
        setSelectedPatientIndex(index);
        setFormData(patients[index]);
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        if (selectedPatientIndex === null) return;
        const patientId = patients[selectedPatientIndex]._id;

        if (!patientId) {
            showToast(t(`toastMessages.INVALID_PATIENT_ID`), ToastType.Error);
            return;
        }

        try {
            if (!UtilsService.validatePhoneNumber(formData.phone)) {
                showToast(t(`toastMessages.FILL_VALID_PHONE`), ToastType.Warning);
                return null;
            }
            
            formData.phone = UtilsService.normalizeIsraeliPhoneNumber(formData.phone) || '';
            const updatedPatient = await PatientService.updatePatient(patientId, formData);

            if (updatedPatient?.error) {
                const code = updatedPatient.error?.response?.data?.code;
                const message = updatedPatient.message;
                showToast(code ? t(`patientMessages.${code}`) : message || 'Failed to add patient', ToastType.Warning);
                return;
            }

            const formattedPatient = UtilsService.formatDateField(
                updatedPatient.data,
                'birthDate'
            );

            setPatients(prev => {
                const updatedList = [...prev];
                updatedList[selectedPatientIndex] = formattedPatient;
                return updatedList;
            });
            
            showToast(t(`patientMessages.${updatedPatient.code}`), ToastType.Success);
            setShowEditModal(false);
            resetForm();
        } catch (error: any) {
            const code = error?.response?.data?.code;
            showToast(code ? t(`patientMessages.${code}`) : 'Failed to update patient',ToastType.Error);
        }
    };

    const handleDelete = (index: number) => {
        setSelectedPatientIndex(index);
        setShowDeleteConfirm(true);
    };

    const handleDeletePatient = async (index: number) => {
        
        const patientId = patients[index]._id;

        if (!patientId) {
            showToast(t(`toastMessages.INVALID_PATIENT_ID`), ToastType.Error);
            return;
        }

        try {
            await PatientService.deletePatient(patientId);
            setPatients((prev) => prev.filter((_, i) => i !== index));
            setTotalPatients(prevTotal => prevTotal - 1); // Update optimistic totalPatients for pagination
            setTotalPages(Math.ceil((totalPatients - 1) / pageSize)); // Update optimistic totalPages for pagination
            setCurrentPage(p => p - 1);
            showToast(t(`toastMessages.PATIENT_DELETED_SUCCESS`), ToastType.Success);
        } catch (error: any) {
            showToast(error.response?.data?.message || "Failed to delete patient", ToastType.Error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const resetForm = () => {
        setFormData({ firstName: '', lastName: '', phone: '', birthDate: '', email: '' });
    };

    const confirmDelete = () => {
        if (selectedPatientIndex != null) {
            handleDeletePatient(selectedPatientIndex);
        }

        setShowDeleteConfirm(false);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
    };

    const getDeleteConfirmationText = () => {
        const selectedPatient = patients[selectedPatientIndex];

        return (
            <>
                {t('are-you-sure-delete-patient')}
                <br />
                <strong>{t('patient')}:</strong>{' '}
                {selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : ''}
                <br />
                {t('this-action-cannot-be-undone')}
            </>
        );
    };

    const handleSendBookingLink = async (index: number) => {
        const patientId = patients[index]._id;

        if (!patientId) {
            showToast(t(`toastMessages.INVALID_PATIENT_ID`), ToastType.Error);
            return;
        }

        try {
            const controller = new AbortController();
            const result = await WhatsAppApiService.sendMessage(
                {
                    type: WhatsAppMessageTypes.BOOKING_LINK_TO_PATIENT,
                    data: {
                        language: i18n.language === 'he' ? 'he' : 'en',
                        patientId,
                        link: `${window.location.origin}/${i18n.language}`
                    }
                },
                controller.signal
            );

            if (result?.error) {
                showToast(
                    t(`whatsappMessages.${result.code}`) || result.message,
                    ToastType.Warning
                );
            } else {
                showToast(
                    t(`whatsappMessages.${result.code}`) ||
                    t(`toastMessages.MESSAGE_SENT_SUCCESS`),
                    ToastType.Success
                );
            }
        } catch (error: any) {
            const code = error?.response?.data?.code;
            showToast(
                code ? t(`whatsappMessages.${code}`) : 'Failed to send message',
                ToastType.Error
            );
        }
    };

    const handleSearch = () => {
        fetchPatients(1, searchTerm); // reset to page 1 whenever searching
    };

    const handlePageSizeChange = (value: number) => {
        setPageSize(value);
        setCurrentPage(1); // if you reset the page after changing size
    };

    return (
        <div>
            <div>
                <div className="row">
                    <div className="col-lg-12 grid-margin stretch-card">
                        <Button className='btn btn-secondary btn-fw' onClick={() => {
                            setShowInviteModal(true);
                            setFormData({  // Reset form data for a new patient
                                firstName: '',
                                lastName: '',
                                phone: '',
                                birthDate: '',
                                email: ''
                            });
                        }}>
                            <UserPlus className="me-2" /> {t('add-patient')}
                        </Button>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-12 grid-margin stretch-card">
                        <div className="card">
                            <div className="card-body">
                                <h4 className="card-title">{t('patient-list')}</h4>
                                <div className="table-responsive">

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="d-flex align-items-center gap-2">
                                            {/* <label className="text-muted small mb-0 me-2">Show:</label> */}
                                            <label className="text-gray form-label">Show:</label>
                                            <SelectCustom
                                                options={pageSizeOptions}
                                                selectedValue={pageSize}
                                                onChange={handlePageSizeChange}
                                                width="5rem"
                                            />
                                        </div>
                                        <label className="text-gray form-label">{totalPatients} total</label>
                                    </div>

                                    <div className="d-flex align-items-center gap-2 mb-3 mt-3">
                                        <div style={{ width: '250px' }}>
                                            <Form.Control
                                                type="text"
                                                className="input-bg-dark text-white border-0 shadow-sm"
                                                placeholder={t('search')}
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <button type="button" onClick={handleSearch} className="btn btn-secondary">
                                                {t('search')}
                                            </button>
                                        </div>
                                    </div>

                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>{t('first-name')}</th>
                                                <th>{t('last-name')}</th>
                                                <th>{t('phone-number')}</th>
                                                <th>{t('birth-date')}</th>
                                                <th>{t('email')}</th>
                                                <th>{t('actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {patients.map((patient, index) => (
                                                <tr key={index}>
                                                    <td>{patient.firstName}</td>
                                                    <td>{patient.lastName}</td>
                                                    <td>{patient.phone}</td>
                                                    <td>{patient.birthDate}</td>
                                                    <td>{patient.email}</td>
                                                    <td>
                                                        <div className="template-demo">
                                                        <button type="button" onClick={() => handleEditPatient(index)} className="btn btn-outline-warning btn-icon-text">
                                                            <i className="mdi  mdi-lead-pencil btn-icon-prepend"></i> {t('edit')}
                                                        </button>
                                                        <button type="button" onClick={() => handleDelete(index)} className="btn btn-outline-danger btn-icon-text">
                                                            <i className="mdi mdi-delete-variant btn-icon-prepend"></i> {t('delete')}
                                                        </button>
                                                        <button type="button" onClick={() => handleSendBookingLink(index)} className="btn btn-outline-success btn-icon-text">
                                                            <i className="mdi mdi-whatsapp btn-icon-prepend"></i> {t('send-booking-link')}
                                                        </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {totalPages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div>
                                                {t('page')} {currentPage} / {totalPages}
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button type="button"
                                                    onClick={() => setCurrentPage(p => p - 1)}
                                                    disabled={currentPage === 1}
                                                    className="btn btn-secondary">
                                                    {t('prev')}
                                                </button>
                                                <button type="button"
                                                    onClick={() => setCurrentPage(p => p + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className="btn btn-secondary">
                                                    {t('next')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)} centered>
                <Modal.Header className="bg-dark text-white custom-border-header" closeButton closeVariant="white">
                    <Modal.Title>{t('add-patient')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white">
                    <Form className="forms-sample">
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('first-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('first-name')} className="input-bg-dark text-white" name="firstName" value={formData.firstName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('last-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('last-name')} className="input-bg-dark text-white" name="lastName" value={formData.lastName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('phone-number')}</Form.Label>
                            <Form.Control type="tel" placeholder={t('phone-number')} className="input-bg-dark text-white" name="phone" value={formData.phone} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('birth-date')}</Form.Label>
                            <Form.Control type="date" placeholder={t('birth-date')} className="input-bg-dark text-white" name="birthDate" value={formData.birthDate} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('email')}</Form.Label>
                            <Form.Control type="email" placeholder={t('email')} className="input-bg-dark text-white" name="email" value={formData.email} onChange={handleInputChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-dark text-white custom-border-footer">
                    <Button variant="secondary" onClick={() => setShowInviteModal(false)} className="d-flex align-items-center">
                        <i className={`mdi mdi-close d-flex align-items-center  ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddPatient}
                        className="d-flex align-items-center ms-2"
                    >
                        <i className={`mdi mdi-content-save d-flex align-items-center ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('add')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header className="bg-dark text-white custom-border-header" closeButton closeVariant="white">
                    <Modal.Title>{t('edit-patient')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white">
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('first-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('first-name')} className="input-bg-dark text-white" name="firstName" value={formData.firstName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('last-name')}</Form.Label>
                            <Form.Control type="text" placeholder={t('last-name')} className="input-bg-dark text-white" name="lastName" value={formData.lastName} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('phone-number')}</Form.Label>
                            <Form.Control type="tel" placeholder={t('phone-number')} className="input-bg-dark text-white" name="phone" value={formData.phone} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('birth-date')}</Form.Label>
                            <Form.Control type="date" placeholder={t('birth-date')} className="input-bg-dark text-white" name="birthDate" value={formData.birthDate} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-gray">{t('email')}</Form.Label>
                            <Form.Control type="email" placeholder={t('email')} className="input-bg-dark text-white" name="email" value={formData.email} onChange={handleInputChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-dark text-white custom-border-footer">
                    <Button variant="secondary" onClick={() => setShowEditModal(false)} className="d-flex align-items-center">
                        <i className={`mdi mdi-close d-flex align-items-center ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveEdit}
                        className="d-flex align-items-center ms-2 px-4"
                    >
                        <i className={`mdi mdi-content-save d-flex align-items-center ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        {t('save-changes')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                show={showDelteConfirm}
                onCancel={cancelDelete}
                onConfirm={confirmDelete}
                title={t('delete-appointment')}
                body={getDeleteConfirmationText()}
                confirmText={t('delete')}
                cancelText={t('cancel')}
            />

        </div>
    );
};

export default Patients;